import { EventTypes, ToastService } from '@ami/angular/core/toasts';
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom, Observable, of } from "rxjs";
import { FeatureFlags } from "./feature-flags";

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsDataService {
  readonly http = inject(HttpClient);
  readonly toastService = inject(ToastService);

  async getFeatureFlags() {
    const isDynamicsFeatureEnabled = await this.isDynamicsFeatureEnabled();
    const isJobBossOrderAutoCreationFeatureEnabled = await this.isJobBossOrderAutoCreationFeatureEnabled();
    const isWorkOrdersFeatureEnabled = await this.isWorkOrdersFeatureEnabled();
    const isCustomerPreferencesFeatureEnabled = await this.isCustomerPreferencesFeatureEnabled();

    const flags: Observable<FeatureFlags> = of({
      Dynamics: isDynamicsFeatureEnabled,
      JobBossOrderAutoCreation:  isJobBossOrderAutoCreationFeatureEnabled,
      WorkOrders: isWorkOrdersFeatureEnabled,
      CustomerPreferences: isCustomerPreferencesFeatureEnabled
    });

    return flags;
  }

  private isDynamicsFeatureEnabled = async () => {
    let isDynamicFeatureEnabled = false;

    try {
      isDynamicFeatureEnabled = (await this.loadDynamicsSettings()) || false;
    } catch (error) {
      this.toastService.queueToast(`An error occurred while loading Dynamics settings.`, EventTypes.Error);
      console.error("An error occurred while loading Dynamics settings.", error);
    }

    return isDynamicFeatureEnabled;
  }

    private isJobBossOrderAutoCreationFeatureEnabled = async () => {
    let isJobBossOrderAutoCreationFeatureEnabled = false;

    try {
      isJobBossOrderAutoCreationFeatureEnabled = (await this.loadJobBossSettings()) || false;
    } catch (error) {
      this.toastService.queueToast(`An error occurred while loading JobBoss settings.`, EventTypes.Error);
      console.error("An error occurred while loading JobBoss settings.", error);
    }

    return isJobBossOrderAutoCreationFeatureEnabled;
  }

  private isWorkOrdersFeatureEnabled = async () => {
    let isWorkOrdersFeatureEnabled = false;

    try {
      isWorkOrdersFeatureEnabled = (await this.loadWorkOrdersSettings()) || false;
    } catch (error) {
      this.toastService.queueToast(`An error occurred while loading Work Orders settings.`, EventTypes.Error);
      console.error("An error occurred while loading Work Orders settings.", error);
    }

    return isWorkOrdersFeatureEnabled;
  }

  private isCustomerPreferencesFeatureEnabled = async () => {
    let isCustomerPreferencesFeatureEnabled = false;

    try {
      isCustomerPreferencesFeatureEnabled = (await this.loadCustomerPreferencesSettings()) || false;
    } catch (error) {
      this.toastService.queueToast(`An error occurred while loading Customer Preferences settings.`, EventTypes.Error);
      console.error("An error occurred while loading Customer Preferences settings.", error);
    }

    return isCustomerPreferencesFeatureEnabled;
  }

  private loadDynamicsSettings = async () => await firstValueFrom(this.http.get<boolean>(`@api/api/feature-management/Dynamics`));
  private loadJobBossSettings = async () => await firstValueFrom(this.http.get<boolean>(`@api/api/feature-management/JobBossOrderAutoCreation`));
  private loadWorkOrdersSettings = async () => await firstValueFrom(this.http.get<boolean>(`@api/api/feature-management/WorkOrders`));
  private loadCustomerPreferencesSettings = async () => await firstValueFrom(this.http.get<boolean>(`@api/api/feature-management/CustomerPreferences`));
}
